import { IContextualMenuItem } from "@fluentui/react";
import React from "react";
import { useSelector } from "react-redux";
import { useLazyUndoCheckoutQuery } from "../../../src/taskpane/components/_v2/redux/api/sharepoint/api-sharepoint-slice";
import { SharePointFolderContentTypeIds } from "../../../src/taskpane/config/constants";
import { RootState } from "../../../src/taskpane/redux";
import authModule from "../../../utilities/AuthModule";
import { LanguageContext } from "../../../utilities/LocalizationModule";
import { menuItemIconStyles } from "../../../src/taskpane/config/controlStyles";
import { ArrowUndo20Regular } from "../../../src/taskpane/components/_v2/components/SvgIcons/ArrowUndo20Regular";

export function useMenuItem_DiscardCheckout(
    selectedItems: any[],
    waitFor: boolean,
    setWaitFor: (waitFor: boolean) => void,
    isInOneDrive: boolean,
    setGridItemIsBusy: (items: any[], isBusy: boolean) => void,
    updateGridItems: (items: any[]) => void,
    setIsCMErrorModalOpen: (isCMErrorModalOpen: boolean) => void,
    setErrorMessage: (errorMessage: string) => void,
) {
    const languageStrings = React.useContext(LanguageContext);
    const currentDirectory = useSelector((state: RootState) => state.workingDirectories.workingDirectories.at(-1));
    const userAccount = useSelector((state: RootState) => state.user.account);

    const [undoCheckout] = useLazyUndoCheckoutQuery();

    const onDiscardCheckoutItem = React.useCallback(() => {
        (async () => {
            setGridItemIsBusy(selectedItems, true);
            setWaitFor(true);

            await Promise.all(selectedItems.map(async item => {
                try {
                    await undoCheckout({
                        siteUrl: currentDirectory.webUrl,
                        uniqueId: item.UniqueId.replace(/[{}]/g, ""),
                        accessToken: isInOneDrive ? await authModule.getOneDriveAccessToken() : await authModule.getSharePointAccessToken(),
                    }).unwrap();
                } catch (error) {
                    console.error("Failed to discard checkout:", error);
                    setIsCMErrorModalOpen(true);
                    setErrorMessage(error?.data?.['odata.error']?.message?.value || "An unknown error occurred.");
                }
            }));

            updateGridItems(selectedItems);
            setWaitFor(false);
        })();
    }, [
        selectedItems,
        currentDirectory,
        isInOneDrive,
        undoCheckout,
        setGridItemIsBusy,
        setWaitFor,
        updateGridItems,
        setIsCMErrorModalOpen,
        setErrorMessage
    ]);

    const menuItem_DiscardCheckout: IContextualMenuItem = React.useMemo(() => {
        const hasSelection = selectedItems.length > 0;
        if (!hasSelection) {
            return null;
        }

        const areAllItemsCheckedOutByCurrentUser = () => {
            if (!selectedItems.every(item => Array.isArray(item.CheckoutUser) && item.CheckoutUser.length > 0)) {
                return false;
            }
            for (const item of selectedItems) {
                for (const user of item.CheckoutUser) {
                    if (user.email !== userAccount.username) {
                        return false;
                    }
                }
            }
            return true;
        };

        const canOverrideCheckout =
            selectedItems.every(x => x.CheckoutUser) &&
            !selectedItems.some(x => x?.EffectiveBasePermissions?.canCancelCheckout === false);

        const selectionContainsNoFolders = !selectedItems.some(x =>
            x.ContentTypeId?.startsWith(SharePointFolderContentTypeIds.folder)
        );

        const shouldShowMenuItem =
            selectionContainsNoFolders &&
            (areAllItemsCheckedOutByCurrentUser() || canOverrideCheckout);

        if (shouldShowMenuItem) {
            return {
                key: 'discardCheckout',
                disabled: waitFor === true,
                text: languageStrings.DiscardCheckOut,
                // iconProps: { iconName: "Undo" },
                iconProps: {},
                onRenderIcon: () => <ArrowUndo20Regular className={menuItemIconStyles} />,
                onClick: onDiscardCheckoutItem,
            };
        }

        return null;

    }, [
        selectedItems,
        waitFor,
        userAccount.username,
        languageStrings,
        onDiscardCheckoutItem
    ]);

    return {
        menuItem_DiscardCheckout
    };
}